import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Clock, Check, AlertTriangle, X } from 'lucide-react'
import { PaymentStatus } from '@/hooks/usePayments'

interface PaymentStatusBadgeProps {
  status: PaymentStatus
  size?: 'sm' | 'md' | 'lg'
  pulse?: boolean
}

const statusConfig: Record<PaymentStatus, { 
  label: string; 
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}> = {
  pending: {
    label: 'Pendente',
    icon: Clock,
    className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30 dark:text-yellow-400',
  },
  paid: {
    label: 'Pago',
    icon: Check,
    className: 'bg-green-500/10 text-green-600 border-green-500/30 dark:text-green-400',
  },
  overdue: {
    label: 'Atrasado',
    icon: AlertTriangle,
    className: 'bg-red-500/10 text-red-600 border-red-500/30 dark:text-red-400',
  },
  cancelled: {
    label: 'Cancelado',
    icon: X,
    className: 'bg-muted text-muted-foreground border-muted',
  },
}

export function PaymentStatusBadge({ status, size = 'md', pulse }: PaymentStatusBadgeProps) {
  const config = statusConfig[status]
  const Icon = config.icon
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5',
  }
  
  return (
    <Badge 
      variant="outline"
      className={cn(
        'font-medium gap-1 border',
        config.className,
        sizeClasses[size],
        pulse && status === 'overdue' && 'animate-pulse'
      )}
    >
      <Icon className={cn(
        size === 'sm' && 'h-3 w-3',
        size === 'md' && 'h-3.5 w-3.5',
        size === 'lg' && 'h-4 w-4',
      )} />
      {config.label}
    </Badge>
  )
}
